import { IBlockData } from '@bay-email/core';
import React, { useMemo } from 'react';
import { IEmailTemplate } from '@/typings';

export interface CollectedBlock {
	label: string;
	helpText: string;
	thumbnail: string;
	data: IBlockData;
	id: string;
}

export interface BlockGroup {
	title: string;
	blocks: Array<CollectedBlock>;
}

export enum AvailableTools {
	MergeTags = 'mergeTags',
	FontFamily = 'fontFamily',
	FontSize = 'fontSize',
	Bold = 'bold',
	Italic = 'italic',
	StrikeThrough = 'strikeThrough',
	Underline = 'underline',
	TextTransform = 'textTransform',
	TextWrap = 'textWrap',
	IconFontColor = 'iconFontColor',
	IconBgColor = 'iconBgColor',
	TableCellBgColor = 'tableCellBgColor',
	Link = 'link',
	Justify = 'justify',
	Lists = 'lists',
	HorizontalRule = 'horizontalRule',
	RemoveFormat = 'removeFormat',
}

export interface PropsProviderProps {
	children?: React.ReactNode;
	height: string;
	fontList?: { value: string; label: string }[];
	onAddCollection?: (payload: CollectedBlock) => void;
	onRemoveCollection?: (payload: { id: string }) => void;
	onUploadImage?: (data: Blob) => Promise<string>;
	interactiveStyle?: {
		hoverColor?: string;
		selectedColor?: string;
		dragoverColor?: string;
		tangentColor?: string;
	};
	autoComplete?: boolean;
	dashed?: boolean;
	socialIcons?: Array<{ content: string; image: string }>;
	/**
	 * Whether the acting user is a BAY global-admin role (System/Global Admin etc.).
	 * Threaded from the host app's access state so editor extensions (e.g. the color
	 * picker's dynamic-copy) can show admin-authoring wording without reaching into a
	 * redux store the editor doesn't own.
	 */
	isGlobalRole?: boolean;
	/**
	 * Whether the editor is being used in Design Manager authoring mode.
	 * This is intentionally distinct from a user's role because ClientBAY/SocialBAY
	 * admins should still get end-user preview behavior.
	 */
	isDesignAdmin?: boolean;

	/**
	 * Reusable-block editor variant. When true, the editor hides the
	 * subject/preview bar and Page attributes, relabels the size indicator to
	 * "Block Size", and treats the canvas as a single fixed Wrapper with no
	 * master-Page level. Threaded through here so vendored editor components
	 * (ToolsPanel, PreviewModal, AttributePanel, EditEmailPreview) can read it
	 * via useEditorProps() without a separate context.
	 */
	blockMode?: boolean;

	/**
	 * Saved reusable blocks, browsable from the Content panel alongside the
	 * built-in ("System") blocks.
	 *
	 * Everything comes from the host: the editor owns the browsing UI and nothing
	 * else. It never fetches — it reports the current filter/search query through
	 * `onQueryChange` and renders whatever `items` come back. That keeps the
	 * record shape, the endpoint and the permission model in the app that owns
	 * them, exactly like every other service the editor stays out of.
	 *
	 * Omit the prop entirely and the System/Block Library toggle does not render,
	 * so a host that has not wired a library is unchanged.
	 */
	blockLibrary?: {
		categoryOptions?: Array<{ label: string; value: string }>;
		companyOptions?: Array<{ label: string; value: string }>;
		items?: Array<{
			id: string;
			name: string;
			categoryLabel?: string;
			thumbnailUrl?: string;
			/**
			 * The block's stored content — a single Wrapper subtree — used as the
			 * drag payload. An item without it renders but cannot be dragged, which
			 * is the honest state for a record whose content the host could not
			 * load, rather than a tile that drops nothing.
			 */
			content?: IBlockData;
		}>;
		loading?: boolean;
		onQueryChange?: (query: {
			search: string;
			category: string;
			company: string;
		}) => void;
		/**
		 * One-shot fetch of the saved blocks in a single category, for the
		 * Choose-a-Block picker. Separate from `onQueryChange`/`items` so the modal
		 * never disturbs the side panel's own list. Returns the same item shape.
		 */
		fetchByCategory?: (category: string) => Promise<
			Array<{
				id: string;
				name: string;
				categoryLabel?: string;
				thumbnailUrl?: string;
				content?: IBlockData;
			}>
		>;
	};

	/**
	 * Recipient-real Preview & Test. When true, the preview modal hides the
	 * user/office test-variable inputs (they are facts about the sender, not
	 * sample data to play with) and renders any dynamic field that has no data
	 * and no fallback as blank — text and images — so the preview shows exactly
	 * what the recipient receives. Host app decides: ClientBAY sets it; Design
	 * Manager does not and keeps editable sample data.
	 */
	recipientPreview?: boolean;

	/**
	 * Whether the acting user's access group may view other users' listings. `false`
	 * hides the Company Listings view in the listing pickers; omitted keeps it.
	 */
	canViewCompanyListings?: boolean;

	mergeTagGenerate?: (m: string) => string;
	onChangeMergeTag?: (ptah: string, val: any) => any;
	renderMergeTagContent?: (props: {
		onChange: (val: string) => void;
		isSelect: boolean;
		value: string;
	}) => React.ReactNode;
	enabledMergeTagsBadge?: boolean;
	mergeTags?: Record<string, any>;
	// Pristine merge-tag defaults WITHOUT kvstore/preview overrides merged in.
	// Used by the Test Variables modal's "Reset to Defaults" so a reset restores
	// the true base sample data instead of the user's previously-saved overrides.
	baseMergeTags?: Record<string, any>;
	/**
	 * The signed-in (or acted-as) account's REAL profile values, keyed by `user.*`
	 * merge-tag leaf — fullName, licenseNumber, companyName, officeAddress, email.
	 *
	 * Kept separate from `mergeTags` because the preview cannot otherwise tell a
	 * real value from the sample one sitting at the same key. The compliance footer
	 * is a legal statement about a real licensee, so it must resolve against these
	 * and not against a hand-edited Test Variable. Empty in Design Manager, where
	 * there is no end user to speak for.
	 */
	realUserProfile?: Record<string, string>;
	/**
	 * The signed-in (or acted-as) agent's real signature HTML, straight from their
	 * profile. Mirrors `realUserProfile`: undefined where no real user exists
	 * (Design Manager); an empty string means a real user with no signature on
	 * file, which the Signature block renders as nothing.
	 */
	realUserEmailSignature?: string;
	previewInjectData?: Record<string, any>;
	onBeforePreview?: (
		html: string,
		mergeTags: PropsProviderProps['previewInjectData'] | PropsProviderProps['mergeTags'],
	) => string | Promise<string>;
	enabledLogic?: boolean;
	locale?: Record<string, string>;

	toolbar?: {
		tools?: AvailableTools[];
		suffix?: (execCommand: (cmd: string, value?: any) => void) => React.ReactNode;
	};

	/**
	 * The template as the host handed it in, BEFORE the open-time block backfill.
	 * EmailEditorProvider already spreads it down here; ReconciliationNotice reads
	 * it to put the pre-update content back when the user undoes.
	 */
	data?: IEmailTemplate;
}

const defaultMergeTagGenerate = (m: string) => `{{${m}}}`;

export const EditorPropsContext = React.createContext<
	PropsProviderProps & {
		mergeTagGenerate: Required<PropsProviderProps['mergeTagGenerate']>;
	}
>({
	children: null,
	height: '100vh',
	fontList: [],
	onAddCollection: undefined,
	onRemoveCollection: undefined,
	onUploadImage: undefined,
	autoComplete: false,
	dashed: true,
	mergeTagGenerate: defaultMergeTagGenerate,
	enabledLogic: false,
});

export const PropsProvider: React.FC<PropsProviderProps> = props => {
	const { dashed = true, mergeTagGenerate = defaultMergeTagGenerate } = props;
	const formatProps = useMemo(() => {
		return {
			...props,
			mergeTagGenerate,
			dashed,
		};
	}, [mergeTagGenerate, props, dashed]);

	return (
		<EditorPropsContext.Provider value={formatProps}>
			{props.children}
		</EditorPropsContext.Provider>
	);
};
